import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "community_popup_dismissed_v2";
const DELAY_MS = 60_000;

/**
 * Pływający popup w lewym dolnym rogu z informacją "Budujemy to razem!".
 * - Pojawia się po 60 s, tylko dla niezalogowanych użytkowników.
 * - Można zamknąć (X) - decyzja zapamiętywana w localStorage.
 * - Czeka na decyzję cookie consent zanim się pojawi.
 */
export const CommunityPopup = () => {
  const { user, loading } = useAuth();
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (loading) return;
    if (user) return; // tylko dla niezalogowanych

    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch {
      // brak dostępu do localStorage - pokaż mimo wszystko
    }

    let timer: number | undefined;
    const schedule = () => {
      timer = window.setTimeout(() => setVisible(true), DELAY_MS);
    };

    const hasCookieDecision = () => {
      try {
        return !!localStorage.getItem("gdzieschronisko_cookie_consent");
      } catch {
        return true;
      }
    };

    if (hasCookieDecision()) {
      schedule();
    } else {
      const onChange = () => {
        window.removeEventListener("cookie-consent-change", onChange);
        schedule();
      };
      window.addEventListener("cookie-consent-change", onChange);
      return () => {
        window.removeEventListener("cookie-consent-change", onChange);
        if (timer) window.clearTimeout(timer);
      };
    }

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [user, loading]);

  const dismiss = () => {
    setClosing(true);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      // ignoruj błąd
    }
    setTimeout(() => {
      setVisible(false);
      setClosing(false);
    }, 250);
  };

  if (!visible) return null;

  return (
    <div
      className={`fixed z-[1200] left-3 sm:left-4 bottom-3 sm:bottom-4 w-[280px] sm:w-[320px] will-change-transform ${closing ? "animate-fade-out" : "animate-popup-rise"}`}
      style={{ paddingBottom: "env(safe-area-inset-bottom)", transformOrigin: "bottom left" }}
      role="status"
      aria-live="polite"
    >
      <div className="relative overflow-hidden rounded-2xl bg-background/85 backdrop-blur-xl border border-primary/15 shadow-[0_10px_40px_-12px_rgba(45,90,61,0.35)] ring-1 ring-black/[0.02]">
        {/* subtle gradient glow */}
        <div
          className="pointer-events-none absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-60 blur-2xl"
          style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.25), transparent 70%)" }}
          aria-hidden="true"
        />
        <div className="relative flex items-start gap-3 p-3.5 pr-9">
          <span
            className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-xl bg-primary/10 text-primary"
            aria-hidden="true"
          >
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-foreground leading-tight mb-0.5">
              Budujemy to razem
            </p>
            <p className="text-[11.5px] text-muted-foreground leading-snug">
              Każdy może aktualizować potrzeby schronisk. Masz świeże info? Kliknij na schronisko i zgłoś aktualizację.
            </p>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="absolute top-2 right-2 p-1 rounded-lg text-muted-foreground/70 hover:text-foreground hover:bg-muted/60 transition-colors"
            aria-label="Zamknij"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
